/**
 * Firestore Thread Adapter — REST client for AI chat thread CRUD.
 *
 * Talks to the backend /api/chat/threads endpoints.
 * Used by the ThreadListSidebar and AiChatProvider for persistence.
 */
import { api } from '@/services/api';

export interface ChatThread {
  id: string;
  title: string;
  status: 'regular' | 'archived';
  pinned: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  messageCount?: number;
  messages?: unknown[];
  /** Bumped by every revert; a save must say which version its messages came from. */
  messagesVersion?: number;
  /** The turn still running on this thread, if any — reattach to it via its stream. */
  activeTurn?: ActiveTurn | null;
}

export interface ActiveTurn {
  turnId: string;
  startedAt: string;
  /** The message that started it; the saved messages only gain it when the turn ends. */
  userMessage?: unknown;
  state?: string;
  /** Started by a background task ending, not by anyone sending (agent-core ai-chat.tasks). */
  wake?: boolean;
}

export interface ThreadListResponse {
  threads: ChatThread[];
  hasMore: boolean;
  nextCursor: string | null;
}

export interface RevertResult {
  keptCount: number;
  /** The thread's message version after the cut. */
  messagesVersion?: number;
  revertedMessageId: string;
  revertedText: string;
  restoredWorkspace: boolean;
  hasCheckpoint: boolean;
  /**
   * The workspace restore runs asynchronously; the file tree updates via a
   * `file-changed` workspace event when it finishes, and is guaranteed complete
   * before the next chat turn.
   */
  workspaceRestorePending?: boolean;
  /** What the scheduled restore changes in your own files — not the snapshot's size. */
  restorePlan?: { copy: number; delete: number };
  /**
   * Set when a requested workspace restore is NOT running, so the UI can say the
   * files were left untouched instead of implying a rollback.  `no-snapshot`:
   * nothing was ever captured at this message.  `stale-snapshot`: the snapshot at
   * this position belongs to a timeline an earlier revert discarded.
   * `suspect-snapshot`: the snapshot is this message's, but was flagged at
   * capture time as not describing a workspace that ever existed.
   */
  restoreSkipped?: 'no-snapshot' | 'stale-snapshot' | 'suspect-snapshot';
}

export interface ForkResult {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  /** new-with-workspace only: the workspace is still copying in the background. */
  workspacePending?: boolean;
}

const BASE = '/chat/threads';

// ── Message versions ────────────────────────────────────────────────────────
//
// The version of each thread's stored transcript that this tab's messages were
// built from. A save sends it, and the server refuses a save from before a
// revert — the browser's end-of-stream save used to land just after a revert
// and put the reverted messages back, where the next turn read them as live.
//
// Recorded only where this tab's messages actually come from the server (the
// thread load, and a revert, after which the runtime cuts its own list to
// match); a thread merely fetched for the sidebar must not move it.
const messagesVersions = new Map<string, number>();

export function noteMessagesVersion(threadId: string, version: unknown): void {
  if (typeof version === 'number' && Number.isFinite(version)) messagesVersions.set(threadId, version);
}

/** Undefined for a thread whose version this tab never learned; its saves go unversioned. */
export function knownMessagesVersion(threadId: string): number | undefined {
  return messagesVersions.get(threadId);
}

// Reverting/forking with a workspace restore copies the whole checkpoint blob
// set back into place server-side.  For large workspaces (hundreds of files,
// multi-MB each) that legitimately runs well past the default 30s request
// timeout — and an abort there leaves the UI unrefreshed while the backend
// keeps working, so the user only sees the result after a manual reload.  Give
// these operations a generous ceiling that matches the backend request budget.
const WORKSPACE_OP_TIMEOUT_MS = 300_000;

export const threadApi = {
  async list(cursor?: string): Promise<ThreadListResponse> {
    const params: Record<string, string> = {};
    if (cursor) params.cursor = cursor;
    const res = await api.get<ThreadListResponse | ChatThread[]>(BASE, params);
    // Backward compat: old backend returns a flat array
    if (Array.isArray(res)) {
      return { threads: res, hasMore: false, nextCursor: null };
    }
    return res;
  },

  async create(data: { title: string; messages: unknown[] }): Promise<ChatThread> {
    return api.post<ChatThread>(BASE, data);
  },

  async get(id: string): Promise<ChatThread> {
    return api.get<ChatThread>(`${BASE}/${id}`);
  },

  async update(id: string, updates: Partial<Pick<ChatThread, 'title' | 'status' | 'messages' | 'pinned' | 'messagesVersion'>>): Promise<ChatThread> {
    return api.patch<ChatThread>(`${BASE}/${id}`, updates);
  },

  async delete(id: string): Promise<void> {
    return api.delete(`${BASE}/${id}`);
  },

  /**
   * Revert the conversation to a message (drop it + everything after).  When
   * `restoreWorkspace` is set, also rolls the workspace files back to their
   * state at that point (requires a checkpoint at-or-before the message).
   */
  async revert(id: string, opts: { messageId: string; restoreWorkspace?: boolean }): Promise<RevertResult> {
    const res = await api.post<RevertResult>(
      `${BASE}/${id}/revert`,
      {
        messageId: opts.messageId,
        restoreWorkspace: !!opts.restoreWorkspace,
      },
      // Workspace restore does per-file GCS copies; only the plain chat-only
      // revert is fast.  Use the generous ceiling whenever a restore is asked.
      opts.restoreWorkspace ? WORKSPACE_OP_TIMEOUT_MS : undefined,
    );
    noteMessagesVersion(id, res.messagesVersion);
    return res;
  },

  /** Fork the conversation at a message into a brand-new thread. */
  async fork(id: string, opts: { messageId: string }): Promise<ForkResult> {
    // Fork copies the source checkpoint's blobs into the new thread server-side.
    return api.post<ForkResult>(`${BASE}/${id}/fork`, { messageId: opts.messageId }, WORKSPACE_OP_TIMEOUT_MS);
  },

  /** Create a fresh, empty thread seeded with a copy of this thread's workspace. */
  async newWithWorkspace(id: string): Promise<ForkResult> {
    return api.post<ForkResult>(`${BASE}/${id}/new-with-workspace`, {});
  },

  /** Poll the background workspace-copy status for a thread (new-with-workspace). */
  async workspaceCopyStatus(id: string): Promise<{ copyPending: boolean; error?: string }> {
    return api.get<{ copyPending: boolean; error?: string }>(`${BASE}/${id}/workspace/status`);
  },
};
